import type { SimEvent } from "../protocol/events";
import { addComposition, maxMultiple, scaleComposition, subtractComposition } from "./composition";
import type { RecipeDef } from "./definitions";
import {
  AMBIENT_TEMP_C,
  isPropertyRecipe,
  newPacket,
  recipesForNode,
  type EngineNode,
  type EngineState,
} from "./state";

/**
 * Reaction resolution for one processing node.
 *
 * A node's whole buffer is treated as one pooled charge: a recipe fires when
 * the pool contains its inputs. Consumed packets are merged into a single
 * product packet, which is what `packet.merged` reports to the renderer.
 */

/**
 * Recipes usable by this node kind, in stable id order. Property recipes
 * (`boilingPoint`/`holdDuration`) describe stream behaviour, not a
 * transformation, so they never fire here — `phase.ts` and `movement.ts`
 * resolve them separately, off the same buffer pool (spec 0003).
 */
function recipesFor(state: EngineState, node: EngineNode): RecipeDef[] {
  return recipesForNode(state, node).filter((r) => !isPropertyRecipe(r));
}

/**
 * A healthy catalytic bed doubles yield (`03-hazards.md` §2.4), per multiple of the
 * recipe fired — `k` firings, each doubled, per spec 0008 resolved open
 * question 2. Once its bed is fully poisoned it reverts to standard output.
 */
function yieldMultiplier(node: EngineNode): number {
  if (node.kind !== "catalyticBed") return 1;
  return node.catalystHealth > 0 ? 2 : 1;
}

export function processReactions(state: EngineState, node: EngineNode, events: SimEvent[]): void {
  if (node.isDestroyed || node.buffer.length === 0) return;
  // `03-hazards.md` §2.5/§3.3: a sludged or infested node reacts at 0.5
  // units/tick too, gated by the same decisions `pushFromNode` reads
  // (`resolveSludgeGate`/`resolveInfestationGate`, tick.ts).
  if (!node.sludgeActiveThisTick || !node.verminActiveThisTick) return;

  const recipes = recipesFor(state, node);
  if (recipes.length === 0) return;

  // Pool the buffer so a recipe can draw across several arrived packets.
  let pooled = node.buffer.reduce((acc, p) => addComposition(acc, p.composition), {});

  for (const recipe of recipes) {
    // The Limiting Reagent Rule (01-simulation-model.md §4, spec 0008): the
    // pool clears k whole multiples of the recipe's ratio, not just one.
    // Low Fire (spec 0035) caps it for the rest of the turn; the surplus stays pooled.
    const uncapped = maxMultiple(pooled, recipe.inputs);
    const k = node.maxMultipleThisTurn === null ? uncapped : Math.min(uncapped, node.maxMultipleThisTurn);
    if (k === 0) continue;

    const consumedIds = node.buffer.map((p) => p.id);
    // "mass is never silently dropped" — the flip side is that contamination
    // travels with it: any consumed packet's impurities are still physically
    // present in the product (folded in via the remainder, below).
    const isContaminated = node.buffer.some((p) => p.isContaminated);
    pooled = subtractComposition(pooled, scaleComposition(recipe.inputs, k));

    // Yield scales with k, and a healthy catalytic bed's ×2 is a bonus per
    // multiple (spec 0008 resolved open question 2), not a flat bonus once.
    const produced = scaleComposition(recipe.outputs, k * yieldMultiplier(node));

    // Whatever the recipe didn't consume stays in the product packet, so mass
    // is never silently dropped.
    const remainder = pooled;
    const productComposition = addComposition(produced, remainder);

    // G-G17: a reaction product is born at ambient, not inherited from its
    // feeds — heating only matters upstream of the reactor (spec 0025).
    const product = newPacket(state, productComposition, {
      isProduct: true,
      isContaminated,
      temperature: AMBIENT_TEMP_C,
    });
    node.buffer = [product];

    events.push({
      type: "reaction.fired",
      nodeId: node.id,
      recipeId: recipe.id,
      multiple: k,
      yieldComposition: { ...produced },
      isExothermic: recipe.isExothermic,
    });

    if (consumedIds.length > 1) {
      events.push({
        type: "packet.merged",
        intoPacketId: product.id,
        consumedPacketIds: consumedIds.filter((id) => id !== product.id),
        at: { ...node.pos },
      });
    }

    if (recipe.isExothermic && recipe.heat > 0) {
      // Heat scales with k too — §2.1 counts each reaction executed, and k of
      // them executed this tick (spec 0008 resolved open question 1).
      node.heatCounter += k * recipe.heat;
      events.push({
        type: "node.heated",
        nodeId: node.id,
        heatCounter: node.heatCounter,
        criticalTemp: node.criticalTemp,
      });
    }

    // Still exactly one recipe id fired per node per tick — only the quantity
    // per firing (k) scales, not the number of distinct recipes considered.
    return;
  }
}
